import React, { useState, useEffect, useMemo, useCallback, Fragment } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faList } from '@fortawesome/free-solid-svg-icons';
import {
  fetchPracticeCaregivers,
  invalidatePracticeCaregiversCache
} from '../../../store/slices/practiceSlice';
import { deleteCaregiver } from '../../../store/slices/caregiverSlice';
import { toast } from 'react-toastify';
import SharedDetailModal from '../../../components/Common/SharedDetailModal/SharedDetailModal';
import DetailsPage from '../../../components/Common/DetailsPage/DetailsPage';
import Table from '../../../components/Common/Table/Table';
import StatusBadge from '../../../components/Common/StatusBadge/StatusBadge';
import ActionButtons from '../../../components/Common/ActionButtons/ActionButtons';
import SearchBar from '../../../components/Common/SearchBar/SearchBar';
import '../AdminManagement/AdminManagement.css';
import './CaregiverManagement.css';
import { buildDetailAddressLines, resolveStateDisplay } from '../../../utils/addressDisplay';

const CaregiverManagement = ({ practiceId, isReadOnly = false }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    caregiversByPracticeId = {},
    practiceCaregiversFetched = {},
    listLoading = {},
    error
  } = useSelector((state) => state.practice ?? {});
  const [searchTerm, setSearchTerm] = useState('');
  const [localSearchTerm, setLocalSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(30);
  const [sortBy, setSortBy] = useState('status');
  const [sortOrder, setSortOrder] = useState('asc');

  const [searchParams] = useSearchParams();
  const viewId = searchParams.get('viewId');

  const cid = practiceId != null ? String(practiceId) : '';
  const hasCache = cid && practiceCaregiversFetched[cid];
  const practiceCaregivers = cid ? caregiversByPracticeId[cid] ?? [] : [];

  useEffect(() => {
    if (practiceId && !practiceCaregiversFetched[String(practiceId)]) {
      dispatch(fetchPracticeCaregivers(practiceId));
    }
  }, [practiceId, dispatch, practiceCaregiversFetched]);

  // Debounce search term for real-time filtering
  useEffect(() => {
    const timer = setTimeout(() => {
      if (localSearchTerm !== searchTerm) {
        setSearchTerm(localSearchTerm);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [localSearchTerm, searchTerm]);

  // Use useMemo for filtering and sorting to prevent unnecessary re-renders and loops
  const filteredCaregivers = useMemo(() => {
    if (!practiceCaregivers || practiceCaregivers.length === 0) return [];

    let filtered = practiceCaregivers.filter(caregiver => {
      const fullName = `${caregiver.first_name || ''} ${caregiver.last_name || ''}`.toLowerCase();
      const email = (caregiver.email || '').toLowerCase();
      const homePhone = caregiver.phone || caregiver.home_phone || '';
      const cellPhone = caregiver.cell_phone || '';
      const searchLower = searchTerm.toLowerCase();

      return fullName.includes(searchLower) ||
        email.includes(searchLower) ||
        homePhone.includes(searchLower) ||
        cellPhone.includes(searchLower);
    });

    // Sort caregivers
    return [...filtered].sort((a, b) => {
      if (sortBy === 'idx') {
        const ai = Number(a.id) || 0;
        const bi = Number(b.id) || 0;
        return sortOrder === 'asc' ? ai - bi : bi - ai;
      }
      if (sortBy === 'status') {
        const statusA = (a.status || 'Active').toLowerCase();
        const statusB = (b.status || 'Active').toLowerCase();

        const statusOrder = { 'active': 1, 'inactive': 2 };
        const orderA = statusOrder[statusA] || 3;
        const orderB = statusOrder[statusB] || 3;

        if (orderA !== orderB) {
          return sortOrder === 'asc' ? orderA - orderB : orderB - orderA;
        }
        const nameA = `${a.first_name || ''} ${a.last_name || ''}`.toLowerCase();
        const nameB = `${b.first_name || ''} ${b.last_name || ''}`.toLowerCase();
        return nameA.localeCompare(nameB);
      } else if (sortBy === 'name') {
        const nameA = `${a.first_name || ''} ${a.last_name || ''}`.toLowerCase();
        const nameB = `${b.first_name || ''} ${b.last_name || ''}`.toLowerCase();
        return sortOrder === 'asc' ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA);
      } else if (sortBy === 'addr') {
        const valueA = String(a.address_line_1 || a.address?.address_line_1 || '').toLowerCase();
        const valueB = String(b.address_line_1 || b.address?.address_line_1 || '').toLowerCase();
        return sortOrder === 'asc' ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA);
      } else if (sortBy === 'home') {
        const valueA = String(a.phone || a.home_phone || '').toLowerCase();
        const valueB = String(b.phone || b.home_phone || '').toLowerCase();
        return sortOrder === 'asc' ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA);
      } else if (sortBy === 'cell') {
        const valueA = String(a.cell_phone || '').toLowerCase();
        const valueB = String(b.cell_phone || '').toLowerCase();
        return sortOrder === 'asc' ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA);
      } else if (sortBy === 'fax') {
        const valueA = String(a.fax || '').toLowerCase();
        const valueB = String(b.fax || '').toLowerCase();
        return sortOrder === 'asc' ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA);
      } else if (sortBy === 'email') {
        const valueA = String(a.email || '').toLowerCase();
        const valueB = String(b.email || '').toLowerCase();
        return sortOrder === 'asc' ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA);
      } else {
        const valueA = a[sortBy] || '';
        const valueB = b[sortBy] || '';
        return sortOrder === 'asc'
          ? String(valueA).localeCompare(String(valueB))
          : String(valueB).localeCompare(String(valueA));
      }
    });
  }, [practiceCaregivers, searchTerm, sortBy, sortOrder]);

  // Reset page only when search or sort parameters change, not on every list update
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, sortBy, sortOrder]);

  // Calculate pagination
  const entriesPerPageNum = entriesPerPage === 'all' ? filteredCaregivers.length : parseInt(entriesPerPage);
  const totalPages = entriesPerPage === 'all' ? 1 : Math.ceil(filteredCaregivers.length / entriesPerPageNum);
  const startIndex = (currentPage - 1) * entriesPerPageNum;
  const endIndex = startIndex + entriesPerPageNum;
  const currentCaregivers = entriesPerPage === 'all' ? filteredCaregivers : filteredCaregivers.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleEntriesChange = (value) => {
    setEntriesPerPage(value);
    setCurrentPage(1);
  };

  const cgStatus = (s) => {
    const v = (s || 'Active').toString().toLowerCase();
    return v === 'active' ? 'Active' : 'Inactive';
  };

  const handleViewCaregiver = (caregiver) => {
    const params = new URLSearchParams(window.location.search);
    params.set('viewId', caregiver.id);
    navigate({ search: params.toString() });
  };

  const closeCaregiverDetails = () => {
    const params = new URLSearchParams(window.location.search);
    params.delete('viewId');
    navigate({ search: params.toString() });
  };

  const handleDetailOverlayClick = (event) => {
    if (event.target === event.currentTarget) {
      closeCaregiverDetails();
    }
  };

  const getDisplayName = (caregiver) => {
    const first = caregiver.first_name || '';
    const last = caregiver.last_name || '';
    if (last && first) return `${last}, ${first}`;
    const single = `${first} ${last}`.trim();
    return single || caregiver.name || 'N/A';
  };

  const getSafeValue = (value) => {
    if (value === null || value === undefined) return 'N/A';
    if (typeof value === 'string' && value.trim() === '') return 'N/A';
    return value;
  };

  /** Street lines, then one line: City, State, Zip (state from name or state_id). */
  const formatAddressLines = (caregiver) =>
    buildDetailAddressLines({
      line1: caregiver.address_line_1 ?? caregiver.address?.address_line_1,
      line2: caregiver.address_line_2 ?? caregiver.address?.address_line_2,
      city: caregiver.city ?? caregiver.address?.city,
      stateDisplay: resolveStateDisplay(caregiver),
      zip: caregiver.zip_code ?? caregiver.zipCode ?? caregiver.address?.zip_code
    });


  const getUsername = (caregiver) =>
    caregiver.username || caregiver.user?.username || caregiver.login_username || 'N/A';

  const getTwoWayAuth = (caregiver) => {
    const status =
      caregiver.is_two_way_auth ??
      caregiver.two_way_authentication_enabled ??
      caregiver.twoWayAuth ??
      caregiver.two_way_auth ??
      caregiver.two_way_authentication ??
      caregiver.two_way_authentication_status;

    if (status === 1 || status === true || status === 'Enabled') return 'Enabled';
    if (status === 0 || status === false || status === 'Disabled') return 'Disabled';

    return 'N/A';
  };

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [caregiverToDelete, setCaregiverToDelete] = useState(null);

  const handleEditCaregiver = (caregiverId) => {
    navigate(`/practices/${practiceId}/add-caregiver?editId=${caregiverId}`);
  };

  const handleDeleteCaregiver = (caregiver) => {
    setCaregiverToDelete(caregiver);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (caregiverToDelete) {
      const idToDelete = caregiverToDelete.id;
      const caregiverName = getDisplayName(caregiverToDelete);
      // Close modal immediately; list removal is handled optimistically in practiceSlice
      setShowDeleteModal(false);
      setCaregiverToDelete(null);
      toast.success(`Caregiver ${caregiverName} deleted successfully`);
      try {
        await dispatch(deleteCaregiver({ practiceId, caregiverId: idToDelete })).unwrap();
      } catch (error) {
        console.error('Error deleting caregiver:', error);
        const errorMessage = error.response?.data?.message || error.message || 'Failed to delete caregiver';
        toast.error(errorMessage);

        // Recovery refresh only on failure to restore consistency.
        dispatch(invalidatePracticeCaregiversCache(practiceId));
        await dispatch(fetchPracticeCaregivers(practiceId));
      }
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setCaregiverToDelete(null);
  };

  const handleAddCaregiver = () => {
    navigate(`/practices/${practiceId}/add-caregiver`);
  };

  const showLoading = practiceId && !hasCache && listLoading.caregivers;

  const handleSort = useCallback(
    (key) => {
      if (sortBy === key) setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
      else {
        setSortBy(key);
        setSortOrder('asc');
      }
    },
    [sortBy]
  );

  const columns = useMemo(
    () => [
      {
        key: 'idx',
        title: '#',
        sortable: true,
        className: 'reusable-table-centered-col',
        render: (_, i) => <span>{startIndex + i + 1}</span>
      },
      {
        key: 'status',
        title: 'Status',
        sortable: true,
        className: 'reusable-table-centered-col',
        render: (row) => <StatusBadge status={cgStatus(row.status)} />
      },
      {
        key: 'name',
        title: 'Name',
        sortable: true,
        render: (row) => (
          <a
            href="#"
            className="admin-management-name-link"
            onClick={(e) => {
              e.preventDefault();
              handleViewCaregiver(row);
            }}
          >
            {getDisplayName(row)}
          </a>
        )
      },
      {
        key: 'addr',
        title: 'Address',
        sortable: true,
        render: (row) => row.address_line_1 || row.address?.address_line_1 || 'N/A'
      },
      { key: 'home', title: 'Home Phone', sortable: true, render: (row) => row.phone || row.home_phone || 'N/A' },
      { key: 'cell', title: 'Cell Phone', sortable: true, render: (row) => row.cell_phone || 'N/A' },
      { key: 'fax', title: 'Fax', sortable: true, render: (row) => row.fax || 'N/A' },
      { key: 'email', title: 'Email', sortable: true, render: (row) => row.email || 'N/A' },
      {
        key: 'action',
        title: 'Action',
        className: 'reusable-table-centered-col',
        render: (row) => (
          <ActionButtons
            onView={() => handleViewCaregiver(row)}
            onEdit={!isReadOnly ? () => handleEditCaregiver(row.id) : null}
            onDelete={!isReadOnly ? () => handleDeleteCaregiver(row) : null}
          />
        )
      }
    ],
    [startIndex, isReadOnly]
  );

  const caregiverDetails = useMemo(() => {
    if (!viewId) return null;
    return practiceCaregivers.find(c => String(c.id) === String(viewId));
  }, [viewId, practiceCaregivers]);

  /** e.g. provider id left in URL when switching tabs — do not show DetailsPage "no record" */
  const staleDetailViewId =
    Boolean(viewId) && Boolean(hasCache) && !showLoading && caregiverDetails === undefined;

  useEffect(() => {
    if (!staleDetailViewId) return;
    const params = new URLSearchParams(window.location.search);
    params.delete('viewId');
    navigate({ search: params.toString() }, { replace: true });
  }, [staleDetailViewId, navigate]);

  if (error && !showLoading) {
    return (
      <div className="alert alert-danger" role="alert">
        {error}
      </div>
    );
  }

  if (viewId && !staleDetailViewId) {
    return (
      <DetailsPage
        title="Practice CareGiver"
        data={caregiverDetails}
        isLoading={showLoading}
        config={[
          {
            placement: 'left',
            fields: [
              { label: 'Name', value: (data) => getDisplayName(data) },
              { label: 'Address', value: (data) => formatAddressLines(data), multiline: true },
              { label: 'Email', value: 'email' },
              { label: 'Home Phone', value: (data) => getSafeValue(data.phone || data.home_phone) },
              { label: 'Cell Phone', value: 'cell_phone' },
              { label: 'Fax', value: 'fax' }
            ]
          },
          {
            placement: 'right',
            fields: [
              { label: 'Username', value: (data) => getUsername(data) },
              { label: 'Two Way Authentication', value: (data) => getTwoWayAuth(data) }
            ]
          }
        ]}
      />
    );
  }

  return (
    <div className="admin-management-container">
      <div className="admin-management-header-row">
        <h2 className="admin-management-title-text">Caregiver List</h2>

        <div className="admin-management-header-actions">
          <SearchBar
            value={localSearchTerm}
            onChange={setLocalSearchTerm}
            className="admin-management-search-wrapper"
          />
          {!isReadOnly && (
            <button className="list-screen-add-btn" onClick={handleAddCaregiver}>
              <FontAwesomeIcon icon={faPlus} className="admin-management-add-icon" />
              Add Caregiver
            </button>
          )}
        </div>
      </div>

      <Table
        columns={columns}
        data={showLoading ? [] : currentCaregivers}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSort={handleSort}
        isLoading={showLoading}
        loadingMode="spinner"
        emptyMessage="No caregivers found"
      />

      {/* Pagination */}
      <div className="list-screen-pagination-section">
        <div className="list-screen-pagination-left">
          <div className="list-screen-entries-selector">
            <span>Show</span>
            <select
              className="list-screen-entries-select"
              style={{ width: '80px' }}
              value={entriesPerPage}
              onChange={(e) => handleEntriesChange(e.target.value)}
            >
              <option value="10">10</option>
              <option value="30">30</option>
              <option value="50">50</option>
              <option value="all">All</option>
            </select>
            <span>entries</span>
          </div>
          <button
            className="list-screen-option-btn"
            onClick={() => navigate(`/event-logs/Caregiver/all?practiceId=${practiceId}`)}
          >
            <FontAwesomeIcon icon={faList} className="admin-management-add-icon" />
            Events Log
          </button>
        </div>

        <div className="list-screen-pagination-right">
          <div className="list-screen-page-selector">
            <span>Page</span>
            <select
              className="list-screen-page-select"
              style={{ width: '60px' }}
              value={currentPage}
              onChange={(e) => handlePageChange(parseInt(e.target.value, 10))}
            >
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                <option key={page} value={page}>{page}</option>
              ))}
            </select>
            <span>of {totalPages}</span>
          </div>
        </div>
      </div>
      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal-overlay">
          <div className="modal-container">
            <div className="modal-header">
              <h5 className="modal-title">Confirm Delete</h5>
              <button
                type="button"
                className="modal-close"
                onClick={cancelDelete}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <p>
                Are you sure you want to delete the caregiver{' '}
                <strong>{getDisplayName(caregiverToDelete)}</strong>?
              </p>
              <p className="modal-warning">
                This action cannot be undone.
              </p>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn-cancel"
                onClick={cancelDelete}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-delete"
                onClick={confirmDelete}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CaregiverManagement;

const DetailRow = ({ label, value, multiline = false }) => (
  <div className="caregiver-management-detail-row">
    <span className="caregiver-management-detail-label">{label}</span>
    <span className="caregiver-management-detail-separator">:</span>
    <span className={`caregiver-management-detail-value ${multiline ? 'caregiver-management-detail-value--multiline' : ''}`}>
      {Array.isArray(value) ? (
        value.map((line, index) => (
          <Fragment key={`${label}-${index}`}>
            {line || 'N/A'}
            {index < value.length - 1 && <br />}
          </Fragment>
        ))
      ) : (
        value || 'N/A'
      )}
    </span>
  </div>
);